import HuskyTooltip from "@husky-hrc/Components/FormFields/HuskyTooltip";
import InputSubText from "@husky-hrc/Components/FormFields/InputSubText";
import RevertButton from "@husky-hrc/Components/FormFields/Revert";
import { ImageInputModel } from "@husky-hrc/Framework/Types/ApiModel";
import { labelStyles } from "@husky-hrc/Utilities/Mixins/form";
import { ProductConfigViewContext } from "@husky-hrc/Widgets/ProductConfigView";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { MouseEvent } from "react";
import styled, { css } from "styled-components";

interface OwnProps {
    input: ImageInputModel;
    onChange: (parameter: string, value: string) => void;
    onUncommit: (parameter: string) => void;
}

export interface ImageButtonStyles {
    containerStyles?: InjectableCss;
    imagesContainerStyles?: InjectableCss;
    imageStyles?: LazyImageProps;
    buttonStyles?: ButtonPresentationProps;
}

const imageButtonStyles: ImageButtonStyles = {
    containerStyles: {
        css: css`
            width: 100%;
            > div:first-child {
                ${labelStyles()}
                display: flex;
                align-items: center;
            }
        `,
    },
    imagesContainerStyles: {
        css: css`
            display: flex;
            gap: 30px;
            flex-wrap: wrap;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        css`
                            justify-content: center;
                        `,
                        null,
                        null,
                        css`
                            justify-content: flex-start;
                        `,
                    ],
                    "min",
                )}
        `,
    },
    imageStyles: {
        css: css`
            img {
                height: 100%;
                width: 100%;
                padding: 20px;
                object-fit: contain;
            }
        `,
    },
};

const ImageCardWrapper = styled.div<{ numValues: number }>`
    max-width: 350px;
    padding: 20px;
    position: relative;
    flex: 0 0 auto;
    ${({ theme, numValues }: { theme: BaseTheme; numValues: number }) =>
        breakpointMediaQueries(
            theme,
            [
                css`
                    width: 100%;
                    min-height: 460px;
                `,
                null,
                null,
                css`
                    width: calc((100% / ${numValues}) - 30px);
                `,
            ],
            "min",
        )}
`;

const ImageContentContainer = styled.div<{ selected: boolean }>`
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    cursor: pointer;
    box-shadow: 0 0 20px 0 rgba(0, 0, 0, 0.2);
    border: 5px solid ${props => (props.selected ? "rgba(116, 164, 248, 0.2)" : "transparent")};
    border-radius: 8px;
    > div {
        display: flex;
        flex-direction: column;
    }
    * {
        pointer-events: none;
    }
`;

const ImageCard = styled.div<{ selected: boolean }>`
    border: ${props => (props.selected ? `2px solid ${props.theme.focus.color}` : "none")};
    border-radius: 8px;
`;

const ValueName = styled.div`
    text-align: center;
    font-size: 18px;
    font-weight: 900;
    letter-spacing: 2px;
    line-height: 22px;
    text-transform: uppercase;
    color: ${getColor("text.main")};
    margin-bottom: 60px;
    ${ImageContentContainer}:hover & {
        color: ${getColor("danger.main")};
    }
    ${ImageContentContainer}[data-selected="true"] & {
        color: ${getColor("danger.main")};
    }
`;

const styles = imageButtonStyles;

type Props = OwnProps;

const ImageButton = ({ input, onChange, onUncommit }: Props) => {
    const [value, setValue] = React.useState(input.value);
    const [errorMessage, setErrorMessage] = React.useState("");
    const productConfigViewContext = React.useContext(ProductConfigViewContext);

    React.useEffect(() => {
        setValue(input.value);
    }, [input]);

    React.useEffect(() => {
        productConfigViewContext.validators[input.name] = validate;
        return () => {
            delete productConfigViewContext.validators[input.name];
        };
    }, [value]);

    const validate = () => {
        if (input.required && !value) {
            setErrorMessage(translate("This field is required"));
            return false;
        }
        setErrorMessage("");
        return true;
    };

    const handleClick = (event: MouseEvent<HTMLDivElement>) => {
        const element = event.target as HTMLInputElement;
        const parameter = element.getAttribute("data-name");
        const value = element.getAttribute("data-value");
        const defaultValue = input.defaultValue.name;

        if ((value !== defaultValue || !input.committed) && parameter && value) {
            onChange(parameter, value);
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        const key = event.nativeEvent.code.toLowerCase();
        if (key === "enter" || key === "space") {
            event.preventDefault();
            const element = event.target as HTMLInputElement;
            element?.click();
        }
    };

    return (
        <StyledWrapper {...styles.containerStyles}>
            <div>
                <span id={`${input.name}_label`} data-required={input.required}>
                    {input.description}
                </span>
                {input.tooltip && <HuskyTooltip input={input} />}
                {input.committed && <RevertButton input={input} onUncommit={onUncommit} />}
            </div>
            <StyledWrapper
                aria-labelledby={`${input.name}_label`}
                aria-describedby={`${input.name}_subText`}
                aria-invalid={!!errorMessage}
                {...styles.imagesContainerStyles}
            >
                {input.values.map((val, index) => (
                    // eslint-disable-next-line react/jsx-key
                    <ImageCardWrapper numValues={input.values.length} key={index}>
                        <ImageContentContainer
                            role="button"
                            tabIndex={0}
                            selected={input.value === val.description}
                            data-name={input.name}
                            data-value={val.name}
                            data-selected={input.value === val.description}
                            onClick={handleClick}
                            onKeyDown={handleKeyDown}
                        >
                            <ImageCard selected={input.value === val.description}>
                                <LazyImage {...styles.imageStyles} src={val.src} altText={val.alt} />
                                <ValueName>{val.description}</ValueName>
                            </ImageCard>
                        </ImageContentContainer>
                    </ImageCardWrapper>
                ))}
            </StyledWrapper>
            <InputSubText id={`${input.name}_subText`} input={input} errorMessage={errorMessage} />
        </StyledWrapper>
    );
};

export default ImageButton;
